import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, useWindowDimensions, Platform, Linking, Image,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import MaterialIcon from 'react-native-vector-icons/MaterialCommunityIcons';
import Toast from 'react-native-toast-message';
import MainLayout from '../../components/ui/MainLayout';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigation, useRoute } from '@react-navigation/native';
import { paymentAPI } from '../../services/apiClient';
import { getSidebarItems } from '../../utils/sidebarItems';

const CERT_PRICE = 2000;
const LS_KEY   = 'safepay_pending';
const LOCK_KEY = 'safepay_lock';

const SAFEPAY_LOGO_BLUE  = require('../../assets/images/safepay-logo-blue.png');
const SAFEPAY_LOGO_WHITE = require('../../assets/images/safepay-logo-white.png');

const PaymentScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { theme, isDark } = useTheme();
  const { user, logout } = useAuth();
  const { width } = useWindowDimensions();
  const { courseId, courseName, action } = route.params || {};

  const [phase, setPhase]             = useState('idle');
  const [tracker, setTracker]         = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [error, setError]             = useState('');
  const [lockBlocked, setLockBlocked] = useState(false);
  const pollRef                       = useRef(null);
  const navigatedRef                  = useRef(false);

  // Fill the content area minus the sidebar (280px on >1024, 250px on >768, 220px otherwise)
  const sidebarW = width > 1024 ? 280 : width > 768 ? 250 : 220;
  const maxWidth = width > 768 ? width - sidebarW - 40 : '100%';

  useEffect(() => {
    if (action !== 'complete' || Platform.OS !== 'web') return;
    if (typeof window === 'undefined') return;
    let pending = null;
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) pending = JSON.parse(raw);
    } catch (_) {}
    if (!pending?.tracker || String(pending.courseId) !== String(courseId)) {
      // No tracker in localStorage — payment was detected via webhook or another tab.
      // Navigate straight to certificate preview since Safepay only redirects here on success.
      localStorage.removeItem(LOCK_KEY);
      navigation.navigate('CertificatePreview', { courseId, courseName });
      return;
    }
    localStorage.removeItem(LS_KEY);
    setPhase('auto-verifying');
    paymentAPI.verifyPayment({ tracker: pending.tracker, courseId })
      .then(res => {
        if (!res.success) throw new Error(res.error || 'Payment not confirmed');
        Toast.show({ type: 'success', text1: 'Payment Successful!', text2: 'Your certificate has been sent to your email', visibilityTime: 3000 });
        setPhase('done');
        setTimeout(() => navigation.navigate('CertificatePreview', { courseId, courseName: pending.courseName || courseName }), 1200);
      })
      .catch(err => { setError(err.message || 'Could not verify payment. Please try again.'); setPhase('idle'); });
  }, []);

  useEffect(() => {
    if (phase !== 'waiting' || !tracker || Platform.OS !== 'web') return;
    if (typeof document === 'undefined') return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !navigatedRef.current) {
        paymentAPI.verifyPayment({ tracker, courseId })
          .then(res => {
            if (res.success && !navigatedRef.current) {
              navigatedRef.current = true;
              clearInterval(pollRef.current);
              if (typeof localStorage !== 'undefined') { localStorage.removeItem(LS_KEY); localStorage.removeItem(LOCK_KEY); }
              setPhase('done');
              Toast.show({ type: 'success', text1: 'Payment Successful!', text2: 'Your certificate has been sent to your email', visibilityTime: 3000 });
              setTimeout(() => navigation.navigate('CertificatePreview', { courseId, courseName }), 1200);
            }
          })
          .catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [phase, tracker]);

  useEffect(() => {
    if (phase !== 'waiting' || !tracker) return;
    const poll = async () => {
      try {
        const res = await paymentAPI.verifyPayment({ tracker, courseId });
        if (res.success && !navigatedRef.current) {
          navigatedRef.current = true;
          clearInterval(pollRef.current);
          if (typeof localStorage !== 'undefined') { localStorage.removeItem(LS_KEY); localStorage.removeItem(LOCK_KEY); }
          setPhase('done');
          Toast.show({ type: 'success', text1: 'Payment Successful!', text2: 'Your certificate has been sent to your email', visibilityTime: 3000 });
          setTimeout(() => navigation.navigate('CertificatePreview', { courseId, courseName }), 1800);
        }
      } catch (_) {}
    };
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [phase, tracker]);

  const handleOpenSafepay = async () => {
    setError('');
    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem(LOCK_KEY);
        if (raw) {
          const lock = JSON.parse(raw);
          const FIFTEEN_MINUTES = 15 * 60 * 1000;
          if (String(lock.courseId) === String(courseId) && Date.now() - lock.ts < FIFTEEN_MINUTES) {
            setLockBlocked(true);
            setError('A payment for this course is already in progress in another tab. Please complete or cancel it first.');
            return;
          }
        }
      } catch (_) {}
      localStorage.setItem(LOCK_KEY, JSON.stringify({ courseId, ts: Date.now() }));
    }
    setPhase('creating');
    try {
      const res = await paymentAPI.createOrder({ courseId, courseName });
      if (!res.success) throw new Error(res.error || 'Could not create payment session');
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        localStorage.setItem(LS_KEY, JSON.stringify({ tracker: res.tracker, courseId, courseName }));
      }
      setTracker(res.tracker);
      setCheckoutUrl(res.checkoutUrl);
      setPhase('waiting');
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.open(res.checkoutUrl, '_blank');
      } else {
        await Linking.openURL(res.checkoutUrl);
      }
    } catch (err) {
      setError(err.message || 'Failed to start payment');
      setPhase('idle');
    }
  };

  const handleReOpen = () => {
    if (!checkoutUrl) return;
    if (Platform.OS === 'web' && typeof window !== 'undefined') window.open(checkoutUrl, '_blank');
    else Linking.openURL(checkoutUrl);
  };

  const handleCancel = () => {
    clearInterval(pollRef.current);
    navigatedRef.current = false;
    if (typeof localStorage !== 'undefined') { localStorage.removeItem(LS_KEY); localStorage.removeItem(LOCK_KEY); }
    setPhase('idle'); setTracker(null); setCheckoutUrl(null); setError(''); setLockBlocked(false);
  };

  const handleClearLockAndRetry = () => {
    if (typeof localStorage !== 'undefined') { localStorage.removeItem(LOCK_KEY); localStorage.removeItem(LS_KEY); }
    setLockBlocked(false); setError('');
    handleOpenSafepay();
  };

  const tileSurface = isDark ? theme.colors.surface : '#fff';
  const tileBorder  = isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0';

  return (
    <MainLayout
      showSidebar={true}
      sidebarItems={getSidebarItems(user?.role)}
      activeRoute="Certificates"
      onNavigate={r => navigation.navigate(r)}
      userInfo={{ name: user?.name, role: user?.role || 'Student', avatar: user?.avatar }}
      onLogout={logout}
    >
      <ScrollView
        contentContainerStyle={[styles.scroll, { maxWidth, alignSelf: 'center', width: '100%' }]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >

        {/* ── Header ── */}
        <View style={styles.topRow}>
          <TouchableOpacity
            style={[styles.backBtn, { backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)' }]}
            onPress={() => navigation.navigate('CertificatePreview', { courseId, courseName })}
          >
            <Icon name="arrow-back" size={18} color={theme.colors.textPrimary} />
          </TouchableOpacity>
          <View style={{ flex: 1 }}>
            <Text style={[styles.headerTitle, { color: theme.colors.textPrimary }]}>Secure Payment</Text>
            <View style={styles.safepayRow}>
              <Icon name="lock-closed" size={10} color="#10B981" />
              <Text style={[styles.poweredBy, { color: theme.colors.textTertiary }]}>Powered by</Text>
              <Image source={isDark ? SAFEPAY_LOGO_WHITE : SAFEPAY_LOGO_BLUE} style={{ width: 58, height: 13 }} resizeMode="contain" />
            </View>
          </View>
        </View>

        {/* ── Bento Grid ── */}
        <View style={styles.bentoGrid}>

          {/* Row 1: Course + Price */}
          <View style={styles.bentoRow}>
            <View style={[styles.bentoCourse, { backgroundColor: tileSurface, borderColor: tileBorder }]}>
              <Text style={styles.bentoLabel}>CERTIFICATE FOR</Text>
              <Text style={[styles.bentoCourseName, { color: theme.colors.textPrimary }]} numberOfLines={2}>
                {courseName}
              </Text>
              <View style={[styles.bentoDivider, { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0' }]} />
              <View style={styles.bentoFeatures}>
                {[
                  { icon: 'file-certificate-outline', text: 'Official signed PDF'     },
                  { icon: 'email-check-outline',      text: 'Delivered to your inbox' },
                  { icon: 'shield-check-outline',     text: 'Unique verifiable ID'    },
                  { icon: 'linkedin',                 text: 'Add to LinkedIn profile' },
                ].map((f, i) => (
                  <View key={i} style={styles.bentoFeatureRow}>
                    <View style={styles.bentoFeatureIcon}>
                      <MaterialIcon name={f.icon} size={14} color="#10B981" />
                    </View>
                    <Text style={[styles.bentoFeatureText, { color: theme.colors.textPrimary }]}>{f.text}</Text>
                  </View>
                ))}
              </View>
            </View>

            <View style={[styles.bentoPrice, { backgroundColor: '#10B981' }]}>
              <View style={styles.bentoPriceTop}>
                <Text style={styles.bentoPriceUnit}>PKR</Text>
                <Text style={styles.bentoPriceValue}>2,000</Text>
              </View>
              <View style={styles.bentoPriceDivider} />
              <Text style={styles.bentoPriceSub}>Certificate Fee</Text>
              <View style={styles.bentoOneTimeBadge}>
                <Text style={styles.bentoOneTimeText}>ONE-TIME</Text>
              </View>
            </View>
          </View>

          {/* Row 2: 4 compact feature chips */}
          <View style={styles.bentoRow}>
            {[
              { icon: 'file-certificate-outline', title: 'Official PDF',    sub: 'Digitally signed',  li: false },
              { icon: 'email-check-outline',      title: 'Instant Email',   sub: 'Sent right away',   li: false },
              { icon: 'shield-check-outline',     title: 'Verifiable',      sub: 'Unique certificate ID', li: false },
              { icon: 'linkedin',                 title: 'LinkedIn Ready',  sub: 'Add to your profile', li: true  },
            ].map((b, i) => (
              <View key={i} style={[styles.bentoChip, {
                backgroundColor: b.li ? (isDark ? 'rgba(10,102,194,0.12)' : '#EFF6FF') : tileSurface,
                borderColor:     b.li ? (isDark ? 'rgba(10,102,194,0.25)' : '#BFDBFE') : tileBorder,
              }]}>
                <View style={[styles.bentoChipIconWrap, {
                  backgroundColor: b.li ? 'rgba(10,102,194,0.15)' : 'rgba(16,185,129,0.12)',
                }]}>
                  <MaterialIcon name={b.icon} size={15} color={b.li ? '#0A66C2' : '#10B981'} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.bentoChipTitle, {
                    color: b.li ? (isDark ? '#93C5FD' : '#1D4ED8') : theme.colors.textPrimary,
                  }]}>{b.title}</Text>
                  <Text style={[styles.bentoChipSub, {
                    color: b.li ? (isDark ? '#60A5FA' : '#3B82F6') : theme.colors.textSecondary,
                  }]}>{b.sub}</Text>
                </View>
              </View>
            ))}
          </View>

        </View>

        {/* ── Error ── */}
        {!!error && (
          <View style={styles.errorRow}>
            <Icon name="alert-circle-outline" size={15} color="#ef4444" />
            <View style={{ flex: 1 }}>
              <Text style={styles.errorText}>{error}</Text>
              {lockBlocked && (
                <TouchableOpacity onPress={handleClearLockAndRetry} style={{ marginTop: 5 }}>
                  <Text style={styles.retryLink}>I closed that tab — retry payment</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        {/* ── How it works ── */}
        {(phase === 'idle' || phase === 'waiting') && (
          <View style={[styles.stepsCard, {
            backgroundColor: isDark ? theme.colors.surface : '#F0FDF8',
            borderColor: isDark ? 'rgba(16,185,129,0.15)' : '#BBF7D0',
          }]}>
            <Text style={[styles.stepsSectionLabel, { color: '#10B981' }]}>HOW IT WORKS</Text>
            {[
              { n: '1', text: 'Click Pay. Safepay opens in a new tab.' },
              { n: '2', text: 'Complete your payment. This page detects it automatically.' },
              { n: '3', text: 'Your certificate lands in your inbox instantly.' },
            ].map(s => (
              <View key={s.n} style={styles.stepRow}>
                <Text style={styles.stepNum}>{s.n}</Text>
                <Text style={[styles.stepText, { color: theme.colors.textPrimary }]}>{s.text}</Text>
              </View>
            ))}
          </View>
        )}

        {/* ── idle: Pay button ── */}
        {phase === 'idle' && (
          <TouchableOpacity style={styles.payBtn} onPress={handleOpenSafepay} activeOpacity={0.87}>
            <MaterialIcon name="credit-card-check" size={20} color="#fff" />
            <Text style={styles.payBtnText}>Pay PKR {CERT_PRICE.toLocaleString()} with Safepay</Text>
          </TouchableOpacity>
        )}

        {/* ── creating ── */}
        {phase === 'creating' && (
          <View style={styles.statusCenter}>
            <ActivityIndicator size="large" color="#10B981" />
            <Text style={[styles.statusTitle, { color: theme.colors.textPrimary }]}>Creating payment session…</Text>
          </View>
        )}

        {/* ── auto-verifying ── */}
        {phase === 'auto-verifying' && (
          <View style={styles.statusCenter}>
            <ActivityIndicator size="large" color="#10B981" />
            <Text style={[styles.statusTitle, { color: theme.colors.textPrimary }]}>Confirming your payment…</Text>
            <Text style={[styles.statusSub, { color: theme.colors.textSecondary }]}>
              You'll be taken to your certificate in a moment.
            </Text>
          </View>
        )}

        {/* ── waiting ── */}
        {phase === 'waiting' && (
          <View style={styles.waitingSection}>
            <View style={styles.waitingIndicatorRow}>
              <ActivityIndicator size="small" color="#10B981" />
              <Text style={[styles.waitingText, { color: '#10B981' }]}>Waiting for payment confirmation…</Text>
            </View>
            <TouchableOpacity
              style={[styles.reOpenBtn, { borderColor: isDark ? 'rgba(16,185,129,0.4)' : '#A7F3D0' }]}
              onPress={handleReOpen} activeOpacity={0.8}
            >
              <Icon name="open-outline" size={15} color="#10B981" />
              <Text style={[styles.reOpenText, { color: '#10B981' }]}>Re-open Safepay tab</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleCancel}>
              <Text style={[styles.cancelText, { color: theme.colors.textTertiary }]}>Cancel payment</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* ── done ── */}
        {phase === 'done' && (
          <View style={[styles.doneCard, {
            backgroundColor: isDark ? 'rgba(16,185,129,0.1)' : '#ECFDF5',
            borderColor: isDark ? 'rgba(16,185,129,0.25)' : '#6EE7B7',
          }]}>
            <Icon name="checkmark-circle" size={52} color="#10B981" />
            <Text style={[styles.doneTitle, { color: isDark ? '#6EE7B7' : '#065F46' }]}>Payment Successful!</Text>
            <Text style={[styles.doneSub, { color: isDark ? '#A7F3D0' : '#047857' }]}>
              Your certificate is being prepared and will be sent to your email shortly.
            </Text>
          </View>
        )}

        <Text style={[styles.secureNote, { color: theme.colors.textTertiary }]}>
          🔒 Payments processed securely by Safepay · SkillSphere does not store your card details
        </Text>

      </ScrollView>
    </MainLayout>
  );
};

const styles = StyleSheet.create({
  scroll: { padding: 20, paddingBottom: 60, gap: 16 },

  // Header
  topRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  backBtn: { width: 38, height: 38, borderRadius: 19, justifyContent: 'center', alignItems: 'center' },
  headerTitle: { fontSize: 17, fontWeight: '700', letterSpacing: -0.3 },
  safepayRow: { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 3 },
  poweredBy: { fontSize: 11 },

  // Bento layout
  bentoGrid: { gap: 10 },
  bentoRow: { flexDirection: 'row', gap: 10, alignItems: 'stretch' },

  // Row 1 — course tile
  bentoCourse: {
    flex: 3, borderRadius: 14, borderWidth: 1,
    borderLeftWidth: 3, borderLeftColor: '#10B981',
    padding: 16, gap: 10,
  },
  bentoLabel: { fontSize: 9, fontWeight: '800', letterSpacing: 1.5, color: '#10B981' },
  bentoCourseName: { fontSize: 15, fontWeight: '700', lineHeight: 21, letterSpacing: -0.2 },
  bentoDivider: { height: 1, marginVertical: 2 },
  bentoFeatures: { gap: 8 },
  bentoFeatureRow: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  bentoFeatureIcon: {
    width: 24, height: 24, borderRadius: 12,
    backgroundColor: 'rgba(16,185,129,0.1)',
    justifyContent: 'center', alignItems: 'center', flexShrink: 0,
  },
  bentoFeatureText: { fontSize: 13, fontWeight: '500', lineHeight: 18, flex: 1 },

  // Row 1 — price tile
  bentoPrice: { flex: 2, borderRadius: 14, padding: 18, justifyContent: 'center' },
  bentoPriceTop: { alignItems: 'flex-start' },
  bentoPriceUnit: { color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: '700', letterSpacing: 1 },
  bentoPriceValue: { color: '#fff', fontSize: 40, fontWeight: '900', letterSpacing: -2, lineHeight: 46, marginTop: 2 },
  bentoPriceDivider: { height: 1, backgroundColor: 'rgba(255,255,255,0.2)', marginVertical: 12 },
  bentoPriceSub: { color: '#fff', fontSize: 14, fontWeight: '700', letterSpacing: 0.2 },
  bentoOneTimeBadge: {
    marginTop: 8, alignSelf: 'flex-start',
    paddingHorizontal: 10, paddingVertical: 4,
    borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.22)',
  },
  bentoOneTimeText: { color: '#fff', fontSize: 10, fontWeight: '800', letterSpacing: 1 },

  // Row 2 — compact chips
  bentoChip: {
    flex: 1, borderRadius: 12, borderWidth: 1,
    padding: 11, flexDirection: 'row', alignItems: 'center', gap: 9,
  },
  bentoChipIconWrap: {
    width: 34, height: 34, borderRadius: 9,
    justifyContent: 'center', alignItems: 'center', flexShrink: 0,
  },
  bentoChipTitle: { fontSize: 12, fontWeight: '800', lineHeight: 16 },
  bentoChipSub: { fontSize: 10, fontWeight: '500', lineHeight: 14, marginTop: 2 },

  // Error
  errorRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 9 },
  errorText: { color: '#ef4444', fontSize: 13, flex: 1, lineHeight: 19 },
  retryLink: { color: '#ef4444', fontSize: 13, fontWeight: '600', textDecorationLine: 'underline' },

  // Steps
  stepsCard: { borderRadius: 14, borderWidth: 1, padding: 16, gap: 10 },
  stepsSectionLabel: { fontSize: 9, fontWeight: '800', letterSpacing: 1.5, marginBottom: 2 },
  stepRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 14 },
  stepNum: { fontSize: 15, fontWeight: '900', color: '#10B981', width: 20 },
  stepText: { flex: 1, fontSize: 14, fontWeight: '500', lineHeight: 21 },

  // Pay button
  payBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 10, paddingVertical: 17, borderRadius: 16,
    backgroundColor: '#10B981',
    shadowColor: '#10B981',
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.4,
    shadowRadius: 14,
    elevation: 7,
  },
  payBtnText: { color: '#fff', fontSize: 16, fontWeight: '700', letterSpacing: 0.2 },

  // Status states
  statusCenter: { alignItems: 'center', paddingVertical: 28, gap: 12 },
  statusTitle: { fontSize: 16, fontWeight: '600', textAlign: 'center' },
  statusSub: { fontSize: 13, textAlign: 'center', lineHeight: 20, maxWidth: 280 },

  waitingSection: { alignItems: 'center', gap: 14, paddingVertical: 6 },
  waitingIndicatorRow: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  waitingText: { fontSize: 14, fontWeight: '600' },
  reOpenBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    paddingVertical: 11, paddingHorizontal: 22, borderRadius: 10, borderWidth: 1.5,
  },
  reOpenText: { fontSize: 14, fontWeight: '600' },
  cancelText: { fontSize: 13, textAlign: 'center' },

  doneCard: {
    borderRadius: 16, borderWidth: 1.5,
    padding: 30, alignItems: 'center', gap: 10,
  },
  doneTitle: { fontSize: 20, fontWeight: '800', textAlign: 'center' },
  doneSub: { fontSize: 13, textAlign: 'center', lineHeight: 20, maxWidth: 280 },

  secureNote: { fontSize: 11, textAlign: 'center', lineHeight: 16 },
});

export default PaymentScreen;
